import { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import './Camera.css';

type CameraState = 'loading' | 'ready' | 'preview' | 'uploading' | 'success' | 'error';

export default function Camera() {
  const { user: _user } = useAuth(); // Used for future user-specific features
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [state, setState] = useState<CameraState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [currentQuestionId, setCurrentQuestionId] = useState<number | null>(null);
  const [uploadMessage, setUploadMessage] = useState<string>('');
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');

  // Start camera stream
  const startCamera = useCallback(async () => {
    setState('loading');
    setError(null);

    try {
      // Stop any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
          setState('ready');
        };
      }
    } catch (err) {
      console.error('Camera error:', err);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('Camera access denied. Please allow camera access in your browser settings.');
        } else if (err.name === 'NotFoundError') {
          setError('No camera found on this device.');
        } else {
          setError(`Camera error: ${err.message}`);
        }
      }
      setState('error');
    }
  }, [facingMode]);

  // Initialize camera and fetch current question
  useEffect(() => {
    startCamera();

    // Get current question ID from server
    api.getCurrentQuestion().then(res => {
      setCurrentQuestionId(res.questionId);
    }).catch(() => {});

    // Cleanup on unmount
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [startCamera]);

  // Capture frame from video
  const captureImage = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || state !== 'ready') return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current frame
    ctx.drawImage(video, 0, 0);

    // Convert to base64 (JPEG for smaller size)
    const imageBase64 = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(imageBase64);
    setState('preview');
  }, [state]);

  // Retake - go back to camera
  const retake = useCallback(() => {
    setCapturedImage(null);
    setState('ready');
  }, []);

  // Keep/Upload the photo
  const keepPhoto = useCallback(async () => {
    if (!capturedImage) return;

    setState('uploading');
    try {
      const result = await api.uploadPhoto(capturedImage, 'image/jpeg');
      setUploadMessage(result.message);
      setState('success');

      // After 2 seconds, go back to ready for more photos
      setTimeout(() => {
        setCapturedImage(null);
        setState('ready');
      }, 2000);
    } catch (err) {
      console.error('Upload failed:', err);
      setError('Failed to upload photo. Please try again.');
      setState('error');
    }
  }, [capturedImage]);

  // Switch between front and back camera
  const switchCamera = useCallback(() => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  }, []);

  return (
    <div className="camera-page">
      <header className="camera-page-header">
        <Link to="/practice" className="back-link">Back to Practice</Link>
        <span className="camera-title">Take Photo</span>
        <button className="btn-icon" onClick={switchCamera} title="Switch camera">
          flip
        </button>
      </header>

      {currentQuestionId && (
        <div className="question-indicator">
          Q#{currentQuestionId}
        </div>
      )}

      <div className="camera-viewport">
        {state === 'loading' && (
          <div className="camera-message">
            <p>Starting camera...</p>
          </div>
        )}

        {state === 'error' && (
          <div className="camera-message error">
            <p>{error}</p>
            <button className="btn btn-primary" onClick={startCamera}>Try Again</button>
          </div>
        )}

        {(state === 'ready' || state === 'loading') && (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="camera-video"
          />
        )}

        {(state === 'preview' || state === 'uploading' || state === 'success') && capturedImage && (
          <div className="preview-container">
            <img src={capturedImage} alt="Captured" className="preview-image" />
            {state === 'uploading' && (
              <div className="preview-overlay">
                <p>Uploading...</p>
              </div>
            )}
            {state === 'success' && (
              <div className="preview-overlay success">
                <p>{uploadMessage}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="camera-controls">
        {state === 'ready' && (
          <button
            className="capture-button"
            onClick={captureImage}
            title="Capture"
          >
            <div className="capture-button-inner" />
          </button>
        )}

        {state === 'preview' && (
          <div className="preview-actions">
            <button className="btn btn-secondary btn-large" onClick={retake}>
              Retake
            </button>
            <button className="btn btn-primary btn-large" onClick={keepPhoto}>
              Keep
            </button>
          </div>
        )}

        {state === 'uploading' && (
          <div className="uploading-indicator">
            Sending...
          </div>
        )}
      </div>

      {/* Hidden canvas for capturing frame */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
}
